import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Checkbox, SELECT_COL_WIDTH } from './primitives';
import { GroupedWords, UseVocabularyWordsViewState } from './useVocabularyWordsView';

interface GroupHeaderProps {
  group: GroupedWords;
  view: UseVocabularyWordsViewState;
  /** Span the full grid (used in cards layout). */
  span?: boolean;
}

const GroupHeader: React.FC<GroupHeaderProps> = ({ group, view, span }) => {
  const ids = group.ids;
  const selCount = ids.filter((id) => view.selected[id]).length;
  const allSelected = selCount === ids.length && ids.length > 0;
  const partial = selCount > 0 && !allSelected;
  // Alphabet & recent groups drop the checkbox and align their label with
  // the same 44 px column the row checkboxes occupy below. The single
  // alphabet letter sits centred inside that column; the wider recent
  // labels (dates / "Нещодавно") share the same `pl: 1` start so both
  // group kinds line up with the centre of the checkbox column visually.
  const isLetterGroup = view.groupBy === 'letter';
  const isRecentGroup = view.groupBy === 'recent';
  const hideCheckbox = isLetterGroup || isRecentGroup;

  return (
    <Stack
      direction={'row'}
      alignItems={'center'}
      spacing={1.5}
      onClick={() => view.toggleMany(ids)}
      title={allSelected ? 'Зняти вибір групи' : 'Вибрати всі слова групи'}
      sx={(t) => ({
        bgcolor: allSelected
          ? t.palette.mode === 'light'
            ? 'rgba(59,44,147,0.07)'
            : 'rgba(155,124,255,0.14)'
          : 'background.default',
        borderBottom: span ? 'none' : '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: span ? 1.5 : 0,
        cursor: 'pointer',
        gridColumn: span ? '1 / -1' : undefined,
        pl: hideCheckbox ? 1 : 2,
        pr: 1,
        py: 1.125,
        transition: 'background-color .12s ease',
        '&:hover': {
          bgcolor:
            t.palette.mode === 'light'
              ? 'rgba(59,44,147,0.07)'
              : 'rgba(155,124,255,0.14)',
        },
      })}
    >
      {!hideCheckbox && (
        <Checkbox
          checked={allSelected}
          indeterminate={partial}
          onClick={() => view.toggleMany(ids)}
        />
      )}
      {isLetterGroup ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: 'none',
            justifyContent: 'center',
            width: SELECT_COL_WIDTH,
          }}
        >
          <Typography
            sx={{
              color: allSelected ? 'primary.main' : 'text.primary',
              fontSize: 14,
              fontWeight: 800,
              letterSpacing: '0.02em',
            }}
          >
            {group.label}
          </Typography>
        </Box>
      ) : (
        <Typography
          sx={{
            color: allSelected ? 'primary.main' : 'text.primary',
            fontSize: 14,
            fontWeight: 800,
            letterSpacing: '0.02em',
          }}
        >
          {group.label}
        </Typography>
      )}
      <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
        {`${group.words.length} ${view.sliv(group.words.length)}${
          selCount ? ` · ${selCount} обрано` : ''
        }`}
      </Typography>
    </Stack>
  );
};

export default GroupHeader;
